import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { IconEdit, IconTrash, IconWorld } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { QuizDefault } from 'icons';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, Typography } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import { LanguageObject } from '@/types';

import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import ManageTranslationsPopup from 'components/popups/ManageTranslationsModal/ManageTranslations';
import { routes } from 'constants/index';
import { mapCategoryToColor } from 'utils/categoryUtils';
import { itemByLanguage } from 'utils/language';

import DeleteQuizPopup from '../BuildQuizzesItems/DeleteQuizPopup';
import RenameQuizPopup from '../BuildQuizzesItems/RenameQuizPopup';

import { getCustomQuizDetailsOptions } from '@/_generated/api-clients/content';
import {
  CustomQuizDetailDefinitionDto,
  CustomQuizDto,
} from '@/_generated/api-clients/xp';
import { quizDraftAtom } from '@/atoms/quiz-draft';

interface QuizCardProps {
  quizDetails: CustomQuizDto;
  onDeleteQuiz: (id: string, name: string) => void;
  onRenameQuiz: (name: string) => void;
  onSaveTranslations: (translations: CustomQuizDetailDefinitionDto) => void;
  setQuiz: (data: CustomQuizDetailDefinitionDto) => void;
}

const QuizCard: React.FC<QuizCardProps> = ({
  quizDetails,
  onDeleteQuiz,
  onRenameQuiz,
  onSaveTranslations,
  setQuiz,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setQuizDraft = useSetAtom(quizDraftAtom);
  const [showDeleteQuizPopup, setShowDeleteQuizPopup] = useState(false);
  const [showRenameQuizPopup, setShowRenameQuizPopup] = useState(false);
  const [showManageTranslationsPopup, setShowManageTranslationsPopup] =
    useState(false);
  const { data } = useAuth();
  const allLocationsWithAtLeastLeaderPermissions =
    data?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const { data: quizData } = useQuery({
    ...getCustomQuizDetailsOptions({
      path: { customQuizId: quizDetails.id ?? '' },
    }),
    enabled:
      (showManageTranslationsPopup || showRenameQuizPopup) && !!quizDetails.id,
  });

  useEffect(() => {
    if (quizData) {
      setQuiz(quizData);
    }
  }, [quizData, setQuiz]);

  const quizName = itemByLanguage(quizDetails.name, '');

  const onShowRenameQuizPopup = () => {
    setShowRenameQuizPopup(true);
  };

  const onDeleteQuizAndClosePopup = () => {
    if (quizDetails.id) {
      onDeleteQuiz(quizDetails.id, quizName);
    }
    setShowDeleteQuizPopup(false);
  };

  const onRenameQuizAndClosePopup = (inputValue: string) => {
    onRenameQuiz(inputValue);
    setShowRenameQuizPopup(false);
  };

  const onShowDeleteQuizPopup = () => {
    setShowDeleteQuizPopup(true);
  };

  const onShowManageTranslationsPopup = () => {
    setShowManageTranslationsPopup(true);
  };

  const goToEditQuizPage = () => {
    // Clear any in-flight quiz draft, since we are intentionally editing
    // a saved quiz.
    setQuizDraft(null);
    navigate({
      pathname: routes.plans.managedQuiz({ quizId: quizDetails.id ?? '' }),
    });
  };

  const backgroundColor = mapCategoryToColor('default');

  return (
    <>
      <Card
        elevation={2}
        className={cn(
          'quizCard relative h-auto animate-[fadeIn_linear_0.3s] flex-row! overflow-visible p-0!',
        )}
      >
        <CardContent
          className={cn(
            'flex grow flex-col items-start justify-center overflow-hidden p-0! sm:flex-row sm:items-center',
          )}
          data-testid="MyQuiz"
        >
          <div className="flex w-full min-w-0 items-stretch justify-between">
            <button
              className="flex min-w-0 flex-1 cursor-pointer text-left"
              onClick={goToEditQuizPage}
              title={quizName}
              type="button"
            >
              <div
                className={cn(
                  'rounded-l-inherit flex shrink-0 items-center justify-center px-4 py-6',
                )}
                style={{ background: backgroundColor }}
              >
                <QuizDefault />
              </div>
              <div className="mx-4 flex min-w-0 flex-1 items-center overflow-hidden">
                <Typography
                  data-testid="QuizName"
                  className={cn(
                    'line-clamp-2 max-w-full font-medium text-ellipsis',
                  )}
                >
                  {quizName}
                </Typography>
              </div>
            </button>
            <div className="flex shrink-0 items-center justify-center pr-6">
              {allLocationsWithAtLeastLeaderPermissions.length && (
                <PopoverMenuButton data-testid="QuizItemsThreeDotMenu">
                  <PopoverMenuButtonItem
                    dataTestId="RenameQuiz"
                    icon={<IconEdit />}
                    onClick={onShowRenameQuizPopup}
                    text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
                  />
                  <PopoverMenuButtonItem
                    dataTestId="ThreeDotMenuManageTranslationsPopup"
                    icon={<IconWorld />}
                    onClick={onShowManageTranslationsPopup}
                    text={t('Generic.editTranslations')}
                  />
                  <PopoverMenuButtonItem
                    dataTestId="ThreeDotMenuDeleteQuizPopup"
                    icon={<IconTrash data-testid="DeleteQuiz" />}
                    isDestructive={true}
                    onClick={onShowDeleteQuizPopup}
                    text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
                  />
                </PopoverMenuButton>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <DeleteQuizPopup
        onDeleteQuizAndClosePopup={onDeleteQuizAndClosePopup}
        quizName={quizName}
        setShowDeleteQuizPopup={setShowDeleteQuizPopup}
        showDeleteQuizPopup={showDeleteQuizPopup}
      />
      <RenameQuizPopup
        onRenameQuizAndClosePopup={onRenameQuizAndClosePopup}
        quizDetails={
          quizDetails as {
            name: LanguageObject;
          }
        }
        setShowRenameQuizPopup={setShowRenameQuizPopup}
        showRenameQuizPopup={showRenameQuizPopup}
      />
      <ManageTranslationsPopup
        isOpen={showManageTranslationsPopup}
        onClose={() => setShowManageTranslationsPopup(false)}
        onSave={updatedTranslations => {
          setShowManageTranslationsPopup(false);
          onSaveTranslations(
            updatedTranslations as CustomQuizDetailDefinitionDto,
          );
        }}
        translatableObject={quizDetails}
      />
    </>
  );
};

export default QuizCard;
